import {loadFulfillments, loadIntegrationStatus, updateFulfillment} from './backend-api.js';
import {renderFulfillments, setStatus} from './render-creator.js';

export function bindFulfillment(root) {
  root.addEventListener('click', async (event) => {
    const tab = event.target.closest('[data-tab]');
    if (tab) {
      switchTab(tab.dataset.tab);
      if (tab.dataset.tab === 'fulfillment') await refreshFulfillments();
      return;
    }
    if (event.target.id === 'refresh-fulfillments') await refreshFulfillments();
    if (event.target.dataset.action === 'select-provider') selectProvider(event.target);
    if (event.target.dataset.action === 'lock-provider') await lockProvider(event.target);
    if (event.target.dataset.action === 'refresh-candidates') await refreshCandidates(event.target);
    if (event.target.dataset.action === 'claim-submission') await claimSubmission(event.target);
    if (event.target.dataset.action === 'copy-payload') await copyPayload(event.target);
  });
  root.addEventListener('submit', handleSubmit);
  root.addEventListener('keydown', handleTabKeydown);
  refreshIntegration();
}

function switchTab(name) {
  document.querySelectorAll('.tab').forEach((tab) => {
    const selected = tab.dataset.tab === name;
    tab.classList.toggle('is-active', selected);
    tab.setAttribute('aria-selected', String(selected));
    tab.tabIndex = selected ? 0 : -1;
  });
  document.querySelectorAll('.panel[role="tabpanel"]').forEach((panel) => {
    panel.hidden = panel.id !== `${name}-panel`;
  });
}

function handleTabKeydown(event) {
  const tab = event.target.closest?.('[role="tab"]');
  if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tabs = [...document.querySelectorAll('[role="tab"]')];
  let index = tabs.indexOf(tab);
  if (event.key === 'Home') index = 0;
  else if (event.key === 'End') index = tabs.length - 1;
  else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
  event.preventDefault();
  switchTab(tabs[index].dataset.tab);
  tabs[index].focus();
  if (tabs[index].dataset.tab === 'fulfillment') refreshFulfillments();
}

async function refreshIntegration() {
  try {
    const result = await loadIntegrationStatus();
    setStatus(
      byId('integration-status'),
      result.ready || result.mode === 'manual'
        ? 'Manual mode is active. Provider production requires an operator action outside this app.'
        : `Setup required: ${(result.missing || []).join(', ') || 'backend bindings'}`,
      result.ready || result.mode === 'manual' ? 'success' : 'warning',
    );
  } catch (error) {
    setStatus(byId('integration-status'), error.message, 'error');
  }
}

async function refreshFulfillments() {
  try {
    const payload = await loadFulfillments();
    renderFulfillments(byId('fulfillment-list'), payload.groups || [], payload.orders || []);
  } catch (error) {
    setStatus(byId('fulfillment-list'), error.message, 'error');
  }
}

function selectProvider(input) {
  const group = input.closest('.fulfillment-group');
  group.querySelectorAll('.candidate-card').forEach((card) =>
    card.classList.toggle('is-selected', card.contains(input)));
  const button = group.querySelector('[data-action="lock-provider"]');
  if (button) button.disabled = false;
}

async function lockProvider(button) {
  const group = button.closest('.fulfillment-group');
  const selected = group.querySelector('input[data-action="select-provider"]:checked');
  if (!selected) return showGroupStatus(group, 'Select an eligible quote first.', 'error');
  await runGroupAction(group, button, () => updateFulfillment('lock-selection', {
    groupId: button.dataset.groupId,
    quoteId: selected.value,
  }), 'Provider decision locked. Claim an exclusive lease before opening the provider portal.');
}

async function copyPayload(button) {
  const group = button.closest('.fulfillment-group');
  try {
    await navigator.clipboard.writeText(decodeURIComponent(button.dataset.payload || ''));
    showGroupStatus(group, 'Order payload copied. The app has not submitted an order.', 'success');
  } catch {
    showGroupStatus(group, 'Clipboard access was unavailable. Copy the values manually.', 'error');
  }
}

async function refreshCandidates(button) {
  const group = button.closest('.fulfillment-group');
  await runGroupAction(group, button, () => updateFulfillment('refresh-candidates', {
    groupId: button.dataset.groupId,
  }), 'Provider candidates refreshed. Review timestamps and hard gates before locking.');
}

async function claimSubmission(button) {
  const group = button.closest('.fulfillment-group');
  const groupId = button.dataset.groupId;
  try {
    button.disabled = true;
    showGroupStatus(group, 'Claiming exclusive submission lease…');
    const claimed = await updateFulfillment('claim-submission', {groupId});
    sessionStorage.setItem(`printew-submission-lease:${groupId}`, claimed.leaseToken);
    await updateFulfillment('authorize-submission', {groupId, leaseToken: claimed.leaseToken});
    showGroupStatus(group, 'Shopify state confirmed. Submit once in the provider portal before this lease expires.', 'success');
    await refreshFulfillments();
  } catch (error) {
    showGroupStatus(group, error.message, 'error');
  } finally {
    if (button.isConnected) button.disabled = false;
  }
}

async function handleSubmit(event) {
  const form = event.target.closest('[data-form]');
  if (!form) return;
  event.preventDefault();
  const values = Object.fromEntries(new FormData(form));
  const submit = form.querySelector('button[type="submit"]');
  if (form.dataset.form === 'external-order') {
    const groupId = form.dataset.groupId;
    const leaseToken = sessionStorage.getItem(`printew-submission-lease:${groupId}`) || '';
    const recorded = await runGroupAction(form.closest('.fulfillment-group'), submit, () =>
      updateFulfillment('record-external-order', {
        groupId,
        providerOrderId: values.providerOrderId.trim(),
        leaseToken,
      }), 'External provider order recorded.');
    if (recorded) sessionStorage.removeItem(`printew-submission-lease:${groupId}`);
  }
  if (form.dataset.form === 'tracking') {
    if (values.confirmed !== 'on') return showGroupStatus(form, 'Confirm the provider shipment first.', 'error');
    await runGroupAction(form.closest('.fulfillment-group'), submit, () =>
      updateFulfillment('confirm-tracking', {
        groupId: form.dataset.groupId,
        carrier: values.carrier.trim(),
        trackingNumber: values.trackingNumber.trim(),
        trackingUrl: values.trackingUrl?.trim() || undefined,
        confirmed: true,
      }), 'Shopify fulfillment created for this shipment group.');
  }
}

async function runGroupAction(group, control, operation, successMessage) {
  try {
    control.disabled = true;
    showGroupStatus(group, 'Saving…');
    await operation();
    showGroupStatus(group, successMessage, 'success');
    await refreshFulfillments();
    return true;
  } catch (error) {
    showGroupStatus(group, error.message, 'error');
    return false;
  } finally {
    if (control.isConnected) control.disabled = false;
  }
}

function showGroupStatus(root, message, tone = '') {
  const group = root.closest?.('.fulfillment-group') || root;
  let status = group.querySelector?.('.group-action-status');
  if (!status) {
    status = document.createElement('div');
    status.className = 'group-action-status status';
    status.setAttribute('aria-live', 'polite');
    group.append(status);
  }
  setStatus(status, message, tone);
}

function byId(id) { return document.getElementById(id); }
